/**
 * validationRules.js
 * Reglas de validación del formulario de residentes.
 */

export const REGEX_PLANO    = /^[A-Za-z]-\d{5}-\d{4}$/;
export const REGEX_PLANO_Z  = /^Z-0000000-\d{4}$/;   // código automático para filiales sin plano
export const REGEX_FOLIO    = /^\d-\d{5}-F-\d{3}$/;
export const REGEX_EMAIL    = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export const REGEX_TEL_CR   = /^[2-9]\d{3}[\s-]?\d{4}$|^\+?[\d\s\-]{7,20}$/;
export const REGEX_CEDULA_CR= /^\d{9,10}$/;
export const REGEX_PLACA_CR = /^[A-Za-z]{1,3}[-\s]?\d{3,6}$/;

function isEmpty(v) { return !v || String(v).trim() === ""; }

// ── Filial ────────────────────────────────────────────────────────────────────
export function validateFilial(filial) {
  const errors = {};

  if (isEmpty(filial.idFilial))
    errors.idFilial = "El ID de Filial es obligatorio.";

  // numPlano es opcional; si está presente debe tener formato válido (real o automático Z-)
  if (!isEmpty(filial.numPlano)) {
    const v = filial.numPlano.trim();
    if (!REGEX_PLANO.test(v) && !REGEX_PLANO_Z.test(v))
      errors.numPlano = "Formato: A-00000-2020 (letra-5dígitos-4dígitos)";
  }

  if (isEmpty(filial.numFolio))
    errors.numFolio = "El Número de Folio es obligatorio.";
  else if (!REGEX_FOLIO.test(filial.numFolio.trim()))
    errors.numFolio = "Formato: 1-10101-F-000 (dígito-5dígitos-F-3dígitos)";

  if (!filial.tipoPropiedad || filial.tipoPropiedad.trim() === "")
    errors.tipoPropiedad = "Seleccioná el tipo de propiedad.";

  return errors;
}

// ── Propietario ───────────────────────────────────────────────────────────────
export function validatePropietario(propietario) {
  const errors = {};

  // Cédula — obligatoria
  if (isEmpty(propietario.cedula))
    errors.cedula = "La cédula es obligatoria.";

  // Nombre OR Nombre Sociedad — al menos uno requerido
  const esSociedad = !isEmpty(propietario.nombre_sociedad);
  const tieneNombre = !isEmpty(propietario.nombre);
  if (!esSociedad && !tieneNombre)
    errors.nombre = "Ingresá el nombre del propietario o el nombre de la sociedad.";

  // Email principal — obligatorio
  if (isEmpty(propietario.emailPrincipal))
    errors.emailPrincipal = "El email principal es obligatorio.";
  else if (!REGEX_EMAIL.test(propietario.emailPrincipal.trim()))
    errors.emailPrincipal = "El email no tiene un formato válido.";

  // Emails extra — solo validar formato si están llenos
  if (propietario.emailExtras?.length) {
    const emailErrors = [];
    let hasEmailError = false;
    propietario.emailExtras.forEach((e, i) => {
      if (e.email && !REGEX_EMAIL.test(e.email.trim())) {
        emailErrors[i] = `Email ${i + 2}: formato inválido.`;
        hasEmailError = true;
      }
    });
    if (hasEmailError) errors.emailExtras = emailErrors;
  }

  // Placa — opcional, solo validar si está llena y no es extranjera
  if (!isEmpty(propietario.placaVehiculo) && !propietario.placaExtranjera) {
    if (!REGEX_PLACA_CR.test(String(propietario.placaVehiculo).trim()))
      errors.placaVehiculo = "Formato CR: ABC-123. Si es extranjera, marcá la casilla.";
  }

  return errors;
}

// ── Copropietario ─────────────────────────────────────────────────────────────
export function validateCopropietario(co) {
  const errors = {};

  if (isEmpty(co.cedula))
    errors.cedula = "La cédula es obligatoria.";

  // Nombre + Apellidos OR Nombre Sociedad
  const esSociedad = !isEmpty(co.nombre_sociedad);
  if (!esSociedad) {
    if (isEmpty(co.nombre))
      errors.nombre = "El nombre es obligatorio (o ingresá Nombre de Sociedad).";
    if (isEmpty(co.apellidos))
      errors.apellidos = "Los apellidos son obligatorios (o ingresá Nombre de Sociedad).";
  }

  // Teléfonos — todos opcionales
  for (const [field, label] of [
    ['telefono_casa', 'Tel. Casa'],
    ['telefono_cel',  'Tel. Celular'],
    ['telefono_trabajo', 'Tel. Trabajo'],
    ['telefono',      'Teléfono'],
  ]) {
    const val = co[field];
    if (!isEmpty(val) && !co.telefonoExtranjero) {
      if (!REGEX_TEL_CR.test(String(val).replace(/\s/g, '')))
        errors[field] = `${label}: formato CR inválido (ej: 8888-1234).`;
    }
  }

  // Placa — opcional
  if (!isEmpty(co.placaVehiculo) && !co.placaExtranjera) {
    if (!REGEX_PLACA_CR.test(String(co.placaVehiculo).trim()))
      errors.placaVehiculo = "Formato CR: ABC-123. Si es extranjera, marcá la casilla.";
  }

  return errors;
}

// ── Autorizado ────────────────────────────────────────────────────────────────
export function validateAutorizado(auth) {
  const errors = {};

  if (isEmpty(auth.cedula))
    errors.cedula = "La cédula es obligatoria.";

  if (isEmpty(auth.nombre))
    errors.nombre = "El nombre es obligatorio.";

  if (isEmpty(auth.apellidos))
    errors.apellidos = "Los apellidos son obligatorios.";

  // Placa — opcional
  if (!isEmpty(auth.placaVehiculo) && !auth.placaExtranjera) {
    if (!REGEX_PLACA_CR.test(String(auth.placaVehiculo).trim()))
      errors.placaVehiculo = "Formato CR: ABC-123. Si es extranjera, marcá la casilla.";
  }

  return errors;
}
